import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { FREQUENCIA_LABELS } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, type DestinatarioAFaturar } from "@/lib/dinheiro";
import { usePermissao } from "@/lib/use-permissao";
import { EmitirExtratoDialog } from "./EmitirExtratoDialog";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";

const moldura = "overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card";
const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/** O que foi entregue e ainda não foi cobrado, por quem vai receber o extrato. */
export function AFaturar() {
  const dados = trpc.financeiro.extratos.aFaturar.useQuery().data;
  const podeEmitir = usePermissao("finance.manage");
  const [alvo, setAlvo] = useState<DestinatarioAFaturar | null>(null);
  const lista = dados?.destinatarios ?? [];
  // Vindo do extrato do cadastro (?emitir=<cliente>): abre o diálogo já nele.
  const [pedido, setPedido] = useState<string | null>(null);
  useParametroDaUrl("emitir", setPedido);
  useEffect(() => {
    if (!pedido || !dados) return;
    const achado = dados.destinatarios.find((d) => d.clientOrganizationId === pedido);
    if (achado && podeEmitir) setAlvo(achado);
    else if (!achado) toast.info("Nada a faturar para este cliente: o que foi entregue já está em extrato.");
    setPedido(null);
  }, [pedido, dados, podeEmitir]);

  return (
    <section className={moldura}>
      <header className="flex items-center justify-between gap-2 border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">A faturar</h3>
        <span className="text-xs text-zinc-500 dark:text-muted-foreground">Entregue e ainda sem extrato</span>
      </header>
      {dados && lista.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nada a faturar: tudo o que foi entregue já está em algum extrato.</p>
      ) : (
        <ul className="divide-y divide-zinc-100 dark:divide-border/50">
          {lista.map((d) => (
            <li key={`${d.clientOrganizationId}|${d.dentistaId ?? ""}`} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
                  {d.nome}
                  {d.dentistaId && <span className="font-normal text-zinc-500 dark:text-muted-foreground"> · {d.clinica}</span>}
                </p>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {d.itens} {d.itens === 1 ? "item" : "itens"} · {FREQUENCIA_LABELS[d.frequencia]}
                  {d.proximoFechamento && ` · próximo fechamento ${diaMes(d.proximoFechamento)}`}
                  {d.semPreco > 0 && <span className="font-semibold text-amber-600"> · {d.semPreco} sem preço</span>}
                </p>
              </div>
              {d.prontos > 0 && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold uppercase text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                  Pronto para emitir · {reais(d.prontoCents)}
                </span>
              )}
              <span className="w-28 text-right text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{reais(d.totalCents)}</span>
              {podeEmitir && (
                <button
                  type="button"
                  onClick={() => setAlvo(d)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-brand-700"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Emitir extrato
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <EmitirExtratoDialog alvo={alvo} onFechar={() => setAlvo(null)} />
    </section>
  );
}
